"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCollectionAnalytics } from "../api";
import type { CollectionAnalyticsResponse } from "../types";

/**
 * `GET /collections/{contract}/analytics` — PHASE 05.
 *
 * WHY IT IS A SHARED HOOK RATHER THAN A SECOND `useQuery` IN EACH CONSUMER.
 * The statistics strip needs volume, sales and average price above the fold,
 * and the Analytics tab needs the same payload plus the charts and the
 * marketplace breakdown. One key means React Query serves both subscribers from
 * ONE request — the same reason `MarketPageClient` reuses `useNfts`' key for its
 * result count instead of firing its own.
 *
 * ENDPOINT BEHAVIOUR, MEASURED (not assumed) before this was mounted at all —
 * that measurement is the Phase 05a analytics gate:
 *   - unauthenticated `GET` returns 200 on every contract tried, so it is public
 *     and NOT permission-gated;
 *   - it always returns the full envelope: `stats` with all seven keys, `charts`
 *     with three dated series, and `marketplaces[]`;
 *   - a collection with no sales returns zeros and empty arrays rather than 404
 *     or a null payload — which is why "empty" here is a POSITIVE test on the
 *     values, never `!analytics`, and why a zero is not printed as a metric;
 *   - the dated series come back in DESCENDING date order, which is why
 *     `SalesChart` sorts before plotting.
 *
 * THE LARGE-SERIES CASE CANNOT BE OBTAINED FROM THIS ENDPOINT. It caps at 15
 * sales, so the longest real series measured was five dated points. Large-series
 * behaviour was therefore validated against a deterministic 365-point-per-series
 * fixture injected through a TEMPORARY test harness that was removed again — see
 * DECISION_LOG D057 for the method, the measurements and its limitations. No
 * fixture code ships here: this hook has exactly one data path, the real one.
 *
 * The conventions are `useCollections`': explicit array key, explicit
 * `staleTime`/`gcTime`, and a `reset()` rather than `refetch()` for the retry
 * control, because `refetch()` was measured to be a no-op on the state a failed
 * browse request leaves behind (D049/D054).
 */
export function useCollectionAnalytics(
  contractAddress: string,
  lang: "fa" | "en",
  options?: { enabled?: boolean }
) {
  const key = ["collection-analytics", contractAddress, lang];
  const queryClient = useQueryClient();

  const query = useQuery<CollectionAnalyticsResponse>({
    queryKey: key,
    queryFn: () => getCollectionAnalytics(contractAddress, lang),
    enabled: options?.enabled !== false && Boolean(contractAddress),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  return {
    ...query,
    analytics: query.data?.data?.analytics,
    reset: () => queryClient.resetQueries({ queryKey: key }),
  };
}
